import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ExamProposalService } from '../../../../services/exam-proposal.service';
import { UserService } from '../../../../services/user.service';
import { AuthStateService } from '../../../../core/states/auth-state.service';

/**
 * Propuestas de examen dentro del detalle del examen:
 *  - Profesor: propone un alumno suyo (alumno + carnet) y ve sus propuestas.
 *  - Admin: ve las propuestas y acepta (asigna) o rechaza.
 */
@Component({
  selector: 'app-exam-proposals-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './exam-proposals-panel.component.html',
})
export class ExamProposalsPanelComponent implements OnInit {
  @Input() proof!: any;

  proposals: any[] = [];
  loading = false;

  // Rol
  isTeacher = false;
  isAdmin = false;

  // Form profesor
  showForm = false;
  students: any[] = [];
  licenses: any[] = [];
  selectedStudentId: number | null = null;
  selectedLicenseId: number | null = null;
  note = '';
  saving = false;

  constructor(
    private service: ExamProposalService,
    private userService: UserService,
    private auth: AuthStateService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.auth.hasRole('admin');
    this.isTeacher = this.auth.isTeacher() && !this.isAdmin;
    this.load();
    if (this.isTeacher) {
      this.userService.getStudents().subscribe({ next: r => (this.students = r.data ?? []) });
    }
  }

  load(): void {
    if (!this.proof?.id) return;
    this.loading = true;
    this.service.list(this.proof.id).subscribe({
      next: r => (this.proposals = r.data ?? []),
      complete: () => (this.loading = false),
    });
  }

  fullName(u: any): string {
    return [u?.name, u?.surname].filter(Boolean).join(' ') || u?.email || 'Alumno';
  }

  onStudentChange(): void {
    this.selectedLicenseId = null;
    this.licenses = [];
    if (this.selectedStudentId) {
      this.userService.getStudentLicenses(this.selectedStudentId).subscribe({
        next: r => (this.licenses = r.data ?? []),
      });
    }
  }

  propose(): void {
    if (!this.selectedStudentId || !this.selectedLicenseId) {
      this.toastr.warning('Selecciona alumno y carnet');
      return;
    }
    this.saving = true;
    this.service.create({
      proof_id: this.proof.id,
      user_id: this.selectedStudentId,
      license_id: this.selectedLicenseId,
      note: this.note?.trim() || undefined,
    }).subscribe({
      next: () => {
        this.toastr.success('Propuesta enviada');
        this.resetForm();
        this.load();
      },
      error: err => {
        this.toastr.error(err?.error?.message ?? 'No se pudo enviar la propuesta');
        this.saving = false;
      },
    });
  }

  resetForm(): void {
    this.showForm = false;
    this.selectedStudentId = null;
    this.selectedLicenseId = null;
    this.note = '';
    this.licenses = [];
    this.saving = false;
  }

  accept(p: any): void {
    this.service.accept(p.id).subscribe({
      next: res => {
        if (res?.success) {
          this.toastr.success('Propuesta aceptada y alumno asignado');
        } else {
          this.toastr.info(res?.message ?? 'Revisa la asignación');
        }
        this.load();
      },
      error: err => this.toastr.error(err?.error?.message ?? 'No se pudo aceptar'),
    });
  }

  reject(p: any): void {
    this.service.reject(p.id).subscribe({
      next: () => { this.toastr.success('Propuesta rechazada'); this.load(); },
      error: err => this.toastr.error(err?.error?.message ?? 'No se pudo rechazar'),
    });
  }

  cancel(p: any): void {
    this.service.remove(p.id).subscribe({
      next: () => { this.toastr.success('Propuesta eliminada'); this.load(); },
      error: err => this.toastr.error(err?.error?.message ?? 'No se pudo eliminar'),
    });
  }

  statusLabel(s: string): string {
    return { pending: 'Pendiente', accepted: 'Aceptada', rejected: 'Rechazada' }[s] ?? s;
  }
}
